/**
 * AdminA2Negotiations.jsx · Panel de Negociaciones de Citas A2.
 * ─────────────────────────────────────────────────────────────────
 * Lista negociaciones activas + timeline por OT + simulador de respuesta
 * del cliente (mock) para probar el motor end-to-end.
 */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import WhatsAppTabs from "../components/WhatsAppTabs";   // v101.192.0
import api from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, RefreshCw, Loader2, User, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const STATE_COLOR = {
  proposal_sent:      { bg: "#FEF3C7", fg: "#92400E", ring: "#FCD34D" },
  alternatives_sent:  { bg: "#DBEAFE", fg: "#1E40AF", ring: "#93C5FD" },
  confirmed:          { bg: "#D1FAE5", fg: "#065F46", ring: "#6EE7B7" },
  manual_review:     { bg: "#FEE2E2", fg: "#991B1B", ring: "#FCA5A5" },
  timed_out:         { bg: "#F3F4F6", fg: "#4B5563", ring: "#D1D5DB" },
};

const STATE_ICON = {
  proposal_sent:      Clock,
  alternatives_sent:  MessageSquare,
  confirmed:          CheckCircle,
  manual_review:      AlertTriangle,
  timed_out:          XCircle,
};

export default function AdminA2Negotiations() {
  const [items, setItems] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyChoice, setReplyChoice] = useState("1");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/a2/negotiations");
      setItems(data.items || []);
      setLabels(data.state_labels || {});
      if (data.items?.length && !selected) loadDetail(data.items[0].id);
    } catch { toast.error("Error cargando negociaciones"); }
    finally { setLoading(false); }
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/a2/negotiations/${id}`);
      setSelected(data);
    } catch { toast.error("Error cargando detalle"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sendReply = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/mock/reply", {
        negotiation_id: selected.id,
        choice: replyChoice,
      });
      toast.success(`Respuesta procesada → ${labels[data.state] || data.state}`);
      await loadDetail(selected.id);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  const scanTimeouts = async () => {
    try {
      const { data } = await api.post("/a2/negotiations/scan-timeouts");
      toast.success(`${data.marked_manual_review} negociaciones marcadas revisión manual`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const canReply = selected && ["proposal_sent", "alternatives_sent"].includes(selected.state);
  const replyOptions = selected?.state === "alternatives_sent" ? ["A", "B", "C", "NO"] : ["1", "2"];

  return (
    <Layout>
      <div className="space-y-4" data-testid="a2-neg-page">
        <WhatsAppTabs />
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#F3E8FF", border: "1px solid #A855F7" }}>
              <MessageSquare size={11} className="text-purple-700"/>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-purple-700">
                Cerebro de Agentes · A2 · Negociaciones
              </span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Negociaciones WhatsApp</h1>
            <p className="mt-1 text-sm text-gray-500">Historial de propuestas de agendamiento enviadas por A2 · Simulador de respuesta cliente.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="neu-btn" data-testid="btn-refresh">
              <RefreshCw size={14}/> Refrescar
            </button>
            <button onClick={scanTimeouts} className="neu-btn" data-testid="btn-scan-timeouts">
              <Clock size={14}/> Escanear timeouts
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
          {/* Lista */}
          <div className="neu-card overflow-hidden">
            <div className="p-3 border-b bg-gray-50 text-[10px] uppercase tracking-widest font-bold text-gray-500">
              {items.length} negociaciones
            </div>
            {loading ? (
              <div className="p-6 text-center"><Loader2 size={16} className="animate-spin mx-auto"/></div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-400">Sin negociaciones aún. Dispara una desde el A2 Dry-Run.</div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                {items.map((n) => {
                  const c = STATE_COLOR[n.state] || STATE_COLOR.timed_out;
                  const Icon = STATE_ICON[n.state] || Clock;
                  const active = selected?.id === n.id;
                  return (
                    <button
                      key={n.id}
                      onClick={() => loadDetail(n.id)}
                      data-testid={`neg-item-${n.id}`}
                      className={`w-full text-left px-3 py-2.5 border-b hover:bg-purple-50 transition ${active ? "bg-purple-50" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-mono text-gray-500">#{n.id}</span>
                            <span className="text-xs font-semibold truncate" style={{ color: "#0A3747" }}>OT {n.task_number || n.odoo_task_id}</span>
                          </div>
                          <div className="text-[11px] text-gray-600 truncate mt-0.5 flex items-center gap-1">
                            <User size={10}/> {n.client_name || "—"}
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono truncate">{n.client_phone || "sin tel"}</div>
                        </div>
                        <div
                          className="px-1.5 py-0.5 rounded flex items-center gap-1 text-[10px] font-semibold flex-shrink-0"
                          style={{ background: c.bg, color: c.fg, border: `1px solid ${c.ring}` }}
                        >
                          <Icon size={10}/> {n.state_label}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detalle */}
          <div className="space-y-3">
            {!selected ? (
              <div className="neu-card p-12 text-center text-gray-400">Selecciona una negociación</div>
            ) : (
              <>
                {/* Cabecera detalle */}
                <div className="neu-card p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-purple-700">Negociación #{selected.id}</div>
                      <div className="text-lg font-bold mt-0.5" style={{ color: "#0A3747" }}>OT {selected.task_number || selected.odoo_task_id}</div>
                      <div className="text-xs text-gray-600 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><User size={11}/> {selected.client_name}</span>
                        <span className="font-mono">{selected.client_phone}</span>
                        <span>Técnico: <b>{selected.technician_email}</b></span>
                      </div>
                    </div>
                    <div>
                      {(() => {
                        const c = STATE_COLOR[selected.state] || STATE_COLOR.timed_out;
                        const Icon = STATE_ICON[selected.state] || Clock;
                        return (
                          <div
                            className="px-2 py-1 rounded flex items-center gap-1.5 text-xs font-bold"
                            style={{ background: c.bg, color: c.fg, border: `1px solid ${c.ring}` }}
                            data-testid={`neg-state-${selected.state}`}
                          >
                            <Icon size={12}/> {selected.state_label}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[11px]">
                    <KV label="Slot inicial" value={selected.initial_slot_start ? new Date(selected.initial_slot_start).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" }) : "—"}/>
                    <KV label="Timeout" value={new Date(selected.timeout_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}/>
                    <KV label="Primer envío" value={new Date(selected.first_message_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}/>
                    <KV label="Última respuesta" value={selected.last_client_reply_at ? new Date(selected.last_client_reply_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" }) : "—"}/>
                  </div>
                  {selected.chosen_slot && (
                    <div className="mt-2 p-2 rounded bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900">
                      <b>Slot elegido:</b> {new Date(selected.chosen_slot.start).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}
                    </div>
                  )}
                  {selected.manual_review_reason && (
                    <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-[11px] text-red-900">
                      <b>Motivo revisión manual:</b> {selected.manual_review_reason}
                    </div>
                  )}
                </div>

                {/* Simulador respuesta */}
                {canReply && (
                  <div className="neu-card p-3 border-l-4" style={{ borderLeftColor: "#7C3AED" }}>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-purple-700 mb-2">Simular respuesta del cliente (MOCK)</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={replyChoice}
                        onChange={(e) => setReplyChoice(e.target.value)}
                        className="px-2 py-1.5 border rounded text-sm font-mono"
                        data-testid="reply-choice"
                      >
                        {replyOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <button onClick={sendReply} disabled={sending} className="neu-btn neu-btn-primary" data-testid="btn-send-reply">
                        {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Enviar
                      </button>
                      <span className="text-[10px] text-gray-500">
                        {selected.state === "proposal_sent" ? "1=Confirmar · 2=Ver alternativas" : "A/B/C=elegir · NO=rechazar todo"}
                      </span>
                    </div>
                  </div>
                )}

                {/* Timeline logs */}
                <div className="neu-card overflow-hidden">
                  <div className="p-2.5 border-b bg-gray-50 text-[10px] uppercase tracking-widest font-bold text-gray-500">
                    Timeline WhatsApp ({selected.logs?.length || 0})
                  </div>
                  <div className="p-3 space-y-2 max-h-[440px] overflow-y-auto" style={{ background: "#F0F2F5" }}>
                    {selected.logs?.map((l) => (
                      <LogBubble key={l.id} log={l}/>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest font-bold text-gray-400">{label}</div>
      <div className="text-xs font-semibold tabular-nums" style={{ color: "#0A3747" }}>{value}</div>
    </div>
  );
}

function LogBubble({ log }) {
  const isOut = log.direction === "out";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-lg shadow-sm p-2.5 relative ${isOut ? "bg-[#DCF8C6]" : "bg-white"}`}
        style={{
          borderTopLeftRadius:  isOut ? "12px" : "2px",
          borderTopRightRadius: isOut ? "2px"  : "12px",
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
        }}
      >
        {log.template_key && (
          <div className="text-[9px] font-mono text-purple-700 mb-1">{log.template_key}</div>
        )}
        <div className="text-xs whitespace-pre-wrap" style={{ color: "#111" }}>
          {log.body}
        </div>
        <div className="text-[9px] text-gray-500 text-right mt-1">
          {new Date(log.created_at).toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit" })}
          {isOut && (
            <span className={`ml-1 ${log.status === "sent" || log.status === "delivered" ? "text-emerald-600" : "text-red-500"}`}>
              {log.status === "sent" ? "✓✓" : log.status === "failed" ? "✗" : "•"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
